// src/composer/share.ts
import { deflateRaw, inflateRaw } from 'pako';
import type { BuildState } from '../packs/types';
import { DEFAULT_COLORS, type ColorState } from './colors';

export interface SharePayload {
  packId: string;
  packVersion: number;
  build: BuildState;
  colors: ColorState;
}

interface WireFormat {
  p: string; v: number; b: BuildState;
  h: number; a: number; gw?: number; dh?: number; th?: number;
  bh?: number; bg?: string;
  sa?: number[];
  br: [1, string] | 0;
  n?: string; t?: string;
}

export function encodeShare(packId: string, packVersion: number, payload: { build: BuildState; colors: ColorState }): string {
  const { build, colors } = payload;
  const wire: WireFormat = {
    p: packId, v: packVersion, b: build,
    h: colors.hue, a: colors.accentHue, gw: colors.glowHue,
    dh: colors.detailHue, th: colors.trimHue,
    bh: colors.backgroundHue,
    sa: [colors.primarySat, colors.accentSat, colors.glowSat, colors.detailSat, colors.trimSat, colors.bgSat],
    br: colors.borderEnabled ? [1, colors.borderColor] : 0,
    n: build.name ?? undefined,
    t: build.tagline ?? undefined,
  };
  const json = JSON.stringify(wire);
  const bytes = new TextEncoder().encode(json);
  const compressed = deflateRaw(bytes);
  const b64 = btoa(Array.from(compressed, c => String.fromCharCode(c)).join(''));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function decodeShare(encoded: string): SharePayload | null {
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
    const decompressed = inflateRaw(bytes);
    const json = new TextDecoder().decode(decompressed);
    const wire: WireFormat = JSON.parse(json);
    const accentHue = wire.a;
    const hue = wire.h;
    const colors: ColorState = {
      hue,
      accentHue,
      glowHue: wire.gw ?? (accentHue + 180) % 360,
      detailHue: wire.dh ?? (hue + 200) % 360,
      trimHue: wire.th ?? DEFAULT_COLORS.trimHue,
      backgroundHue: wire.bh ?? DEFAULT_COLORS.backgroundHue,
      primarySat: wire.sa?.[0] ?? 100,
      accentSat: wire.sa?.[1] ?? 100,
      glowSat: wire.sa?.[2] ?? 100,
      detailSat: wire.sa?.[3] ?? 100,
      trimSat: wire.sa?.[4] ?? 100,
      bgSat: wire.sa?.[5] ?? 100,
      borderEnabled: wire.br !== 0,
      borderColor: Array.isArray(wire.br) ? wire.br[1] : '#000000',
    };
    return {
      packId: wire.p,
      packVersion: wire.v,
      build: { ...wire.b, name: wire.n, tagline: wire.t },
      colors,
    };
  } catch {
    return null;
  }
}

export function slugify(name: string | undefined, fallback = 'ship'): string {
  if (!name) return fallback;
  const s = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return s || fallback;
}
